import React, { useState, useContext, useEffect } from "react";
import AuthContext from "../context/AuthContext";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { Lock, CheckCircle, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import authService from "../services/authService";
import { useTranslation } from "../i18n";

const PasswordReset = () => {
  const { t } = useTranslation("auth");
  const { user, isInitialized } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [formData, setFormData] = useState({
    new_password: "",
    confirm_password: ""
  });
  const [errors, setErrors] = useState({});
  const [serverError, setServerError] = useState("");
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [token, setToken] = useState("");

  // Get token from URL query params
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const tokenParam = searchParams.get("token");

    if (!tokenParam) {
      setServerError(t("reset.errors.missing_token"));
    } else {
      setToken(tokenParam);
    }
  }, [location, t]);

  // Redirect if already logged in
  useEffect(() => {
    if (isInitialized && user) {
      navigate("/", { replace: true });
    }
  }, [user, navigate, isInitialized]);

  const validateForm = () => {
    const newErrors = {};

    // Password validation
    if (!formData.new_password) {
      newErrors.new_password = t("validation.password_required");
    } else if (formData.new_password.length < 8) {
      newErrors.new_password = t("validation.password_min_length");
    }

    // Confirm password validation
    if (!formData.confirm_password) {
      newErrors.confirm_password = t("validation.confirm_password_required");
    } else if (formData.new_password !== formData.confirm_password) {
      newErrors.confirm_password = t("validation.passwords_mismatch");
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: "" }));
    }
    setServerError("");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!token) {
      setServerError(t("reset.errors.invalid_token"));
      return;
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setServerError("");

    try {
      await authService.resetPassword(token, formData.new_password);
      setSuccess(true);

      // Redirect to login after 2 seconds
      setTimeout(() => {
        navigate("/login", {
          state: { message: t("reset.success_redirect_message") }
        });
      }, 2000);
    } catch (err) {
      const errorMessage = err.response?.data?.detail ||
                          err.response?.data?.message ||
                          t("reset.errors.reset_failed");
      setServerError(errorMessage);
      setLoading(false);
    }
  };

  // Show loading while checking auth state
  if (!isInitialized) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-white p-4">
      {/* Logo or App Name */}
      <div className="mb-8 text-center">
        <div className="flex items-center justify-center mb-2">
          <h1 className="text-3xl font-bold org-gradient-text">
            AddFlint
          </h1>
        </div>
      </div>

      {/* Password Reset Card */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-white p-8 rounded-2xl shadow-md w-full max-w-md border border-gray-200"
      >
        <h2 className="text-2xl font-bold mb-2 text-gray-800">{t("reset.title")}</h2>
        <p className="text-sm text-gray-600 mb-6">
          {t("reset.description")}
        </p>

        {/* Success Message */}
        {success && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mb-6 p-3 bg-green-50 border border-green-200 rounded-2xl flex items-center text-green-700"
          >
            <CheckCircle className="w-5 h-5 mr-2" />
            <p className="text-sm">{t("reset.success")}</p>
          </motion.div>
        )}

        {/* Error Message */}
        {serverError && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "auto" }}
            className="mb-6 p-3 bg-red-50 border border-red-200 rounded-2xl"
          >
            <div className="flex items-start">
              <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 text-red-600 mt-0.5" />
              <div>
                <p className="text-sm text-red-700">{serverError}</p>
                {serverError.includes("expired") && (
                  <Link
                    to="/password-recovery"
                    className="text-xs font-medium text-red-700 hover:text-red-800 underline mt-2 inline-block"
                  >
                    {t("reset.request_new_link")}
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        )}

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* New Password Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 block">{t("fields.new_password")}</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                name="new_password"
                placeholder="••••••••"
                value={formData.new_password}
                onChange={handleChange}
                disabled={!token || success}
                className={`w-full pl-10 pr-3 py-2 border rounded-full focus:outline-none focus:ring-2 ${
                  errors.new_password
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"
                } ${(!token || success) ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
            </div>
            {errors.new_password && (
              <p className="text-xs text-red-600 mt-1">{errors.new_password}</p>
            )}
          </div>

          {/* Confirm Password Input */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-gray-700 block">{t("fields.confirm_password")}</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Lock className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="password"
                name="confirm_password"
                placeholder="••••••••"
                value={formData.confirm_password}
                onChange={handleChange}
                disabled={!token || success}
                className={`w-full pl-10 pr-3 py-2 border rounded-full focus:outline-none focus:ring-2 ${
                  errors.confirm_password
                    ? "border-red-300 focus:ring-red-500 focus:border-red-500"
                    : "border-gray-300 focus:ring-purple-500 focus:border-purple-500"
                } ${(!token || success) ? "bg-gray-100 cursor-not-allowed" : ""}`}
              />
            </div>
            {errors.confirm_password && (
              <p className="text-xs text-red-600 mt-1">{errors.confirm_password}</p>
            )}
          </div>

          {/* Reset Password Button */}
          <button
            type="submit"
            disabled={loading || success || !token}
            className={`w-full py-3 px-4 rounded-full text-white font-medium flex items-center justify-center transition-all ${
              loading || success || !token
                ? "bg-gray-400 cursor-not-allowed"
                : "org-gradient shadow-md hover:shadow-lg"
            }`}
          >
            {loading ? (
              <>
                <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                {t("reset.resetting")}
              </>
            ) : success ? (
              <>
                <CheckCircle className="w-5 h-5 mr-2" />
                {t("reset.done")}
              </>
            ) : (
              t("reset.submit")
            )}
          </button>
        </form>

        {/* Login Link */}
        <p className="mt-8 text-center text-sm text-gray-600">
          {t("reset.remember_password")}{" "}
          <Link to="/login" className="font-medium text-purple-600 hover:text-purple-500">
            {t("reset.back_to_login")}
          </Link>
        </p>
      </motion.div>

      {/* Footer */}
      <p className="mt-8 text-center text-xs text-gray-500">
        {t("footer.copyright", { year: 2025 })}
      </p>
    </div>
  );
};

export default PasswordReset;
